import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { MapProvider } from '@/components/MapProvider';
import { DeliveryMap } from '@/components/DeliveryMap';
import { useRealTimeTracking } from '@/hooks/useRealTimeTracking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MapPin, Clock, Package, CheckCircle, Truck, Navigation, User, ArrowLeft, Copy, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const OrderTrackingRealTime = ({ user, onLogout }) => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentLocation, routeHistory, connectionStatus, isConnected } = useRealTimeTracking(orderId);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await axios.get(`/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error('Error al cargar pedido');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const fetchTrackingData = useCallback(async () => {
    try {
      const response = await axios.get(`/tracking/order/${orderId}`);
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking:', error);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    fetchTrackingData();
    const interval = setInterval(() => {
      fetchOrder();
      fetchTrackingData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchOrder, fetchTrackingData]);

  const getStatusSteps = () => [
    { status: 'created', label: 'Creado', icon: Package, color: '#6B7280' },
    { status: 'ready_for_pickup', label: 'Listo', icon: Clock, color: '#F59E0B' },
    { status: 'in_custody', label: 'En Custodia', icon: Truck, color: '#3B82F6' },
    { status: 'en_route', label: 'En Ruta', icon: Navigation, color: '#8B5CF6' },
    { status: 'delivered', label: 'Entregado', icon: CheckCircle, color: '#10B981' }
  ];

  const getCurrentStepIndex = (status) => {
    const statusMap = {
      'CREATED': 0,
      'READY_FOR_PICKUP': 1,
      'IN_CUSTODY': 2,
      'EN_ROUTE': 3,
      'DELIVERED': 4
    };
    return statusMap[status?.toUpperCase()] ?? 0;
  };

  const copyTrackingLink = () => {
    const trackingUrl = `${window.location.origin}/track/${orderId}`;
    navigator.clipboard.writeText(trackingUrl);
    toast.success('Link de seguimiento copiado');
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} title="Tracking">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#224049]"></div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout user={user} onLogout={onLogout} title="Tracking">
        <div className="text-center py-12">
          <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <div className="text-lg text-red-600 mb-4">Pedido no encontrado</div>
          <Button asChild variant="outline">
            <Link to="/orders">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Pedidos
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const currentStepIndex = getCurrentStepIndex(order.status);
  const steps = getStatusSteps();
  const riderLocation = currentLocation || trackingData?.current_location;

  // Prepare delivery stop for map
  const deliveryStop = order.delivery_address ? {
    id: order.id,
    address: order.delivery_address.line1,
    latitude: order.delivery_address.lat || order.delivery_address.latitude || 0,
    longitude: order.delivery_address.lng || order.delivery_address.longitude || 0,
    status: order.status === 'DELIVERED' ? 'completed' : order.status === 'EN_ROUTE' ? 'in_progress' : 'pending',
    customer_name: order.customer?.name
  } : null;

  const trackingUrl = `${window.location.origin}/track/${orderId}`;

  return (
    <Layout user={user} onLogout={onLogout} title="Tracking de Pedido">
      <div className="p-4 md:p-6 bg-[#EAE0D5] min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" asChild className="text-[#224049]">
                <Link to="/orders">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Volver
                </Link>
              </Button>
              <h1 className="text-2xl md:text-3xl font-bold text-[#224049]">
                Seguimiento en Tiempo Real
              </h1>
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchOrder(); fetchTrackingData(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Order Info */}
            <div className="lg:col-span-1 space-y-4">
              {/* Order Header */}
              <Card className="bg-white">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[#224049]">
                      Pedido #{order.id?.substring(0, 8)}
                    </CardTitle>
                    <Badge className={order.mode === 'EXPRESS' ? 'bg-[#C38154]' : 'bg-[#0F7A5B]'}>
                      {order.mode === 'EXPRESS' ? 'Express 60\'' : 'Estándar'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 mt-1 text-[#C38154]" />
                    <div>
                      <p className="text-sm font-medium">Cliente</p>
                      <p className="text-sm text-gray-600">{order.customer?.name}</p>
                      <p className="text-xs text-gray-500">{order.customer?.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-1 text-[#C38154]" />
                    <div>
                      <p className="text-sm font-medium">Dirección</p>
                      <p className="text-sm text-gray-600">{order.delivery_address?.line1}</p>
                      <p className="text-xs text-gray-500">
                        {order.delivery_address?.city}, {order.delivery_address?.postal_code}
                      </p>
                    </div>
                  </div>

                  {order.eta_minutes && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 mt-1 text-[#C38154]" />
                      <div>
                        <p className="text-sm font-medium">ETA</p>
                        <p className="text-sm text-gray-600">{order.eta_minutes} minutos</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Status Timeline */}
              <Card className="bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#224049] text-lg">Estado de Seguimiento</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {steps.map((step, index) => {
                      const StepIcon = step.icon;
                      const isActive = index <= currentStepIndex;
                      const isCurrent = index === currentStepIndex;

                      return (
                        <div key={step.status} className="flex items-center gap-3">
                          <div
                            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                              isActive ? 'bg-[#224049]' : 'bg-gray-200'
                            } ${isCurrent ? 'ring-2 ring-[#D4AD6A] ring-offset-2' : ''}`}
                          >
                            <StepIcon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${isActive ? 'text-[#224049]' : 'text-gray-400'}`}>
                              {step.label}
                            </p>
                            {isCurrent && (
                              <p className="text-xs text-[#C38154] animate-pulse">En progreso</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Connection indicator */}
                  {trackingData?.tracking_active && (
                    <div className="mt-4 p-3 bg-[#F5F1ED] rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                        }`} />
                        <p className="text-xs text-gray-600">
                          {isConnected ? 'Conectado en tiempo real' : 'Reconectando...'}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Route Info */}
              {order.route_distance_meters && (
                <Card className="bg-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-[#224049] text-lg">Información de Ruta</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Distancia:</span>
                      <span className="font-medium">{(order.route_distance_meters / 1000).toFixed(2)} km</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duración:</span>
                      <span className="font-medium">{Math.round(order.route_duration_seconds / 60)} min</span>
                    </div>
                    {order.route_order !== null && order.route_order !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Orden en ruta:</span>
                        <span className="font-medium">Parada #{order.route_order + 1}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Share Tracking Link */}
              <Card className="bg-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#224049] text-lg">Compartir Seguimiento</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Comparte este link con el cliente para que pueda ver el estado de su pedido:
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      value={trackingUrl} 
                      readOnly 
                      className="text-xs bg-gray-50"
                    />
                    <Button size="sm" variant="outline" onClick={copyTrackingLink}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Abrir Vista Pública
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Map */}
            <Card className="lg:col-span-2 bg-white h-[600px] md:h-[700px]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#224049] flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#C38154]" />
                  Ubicación en Tiempo Real
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 h-[calc(100%-60px)]">
                <MapProvider>
                  <DeliveryMap
                    stops={deliveryStop ? [deliveryStop] : []}
                    currentLocation={riderLocation}
                    encodedPolyline={order.route_polyline}
                    showRouteHistory={true}
                    routeHistory={routeHistory}
                    centerOnRider={trackingData?.tracking_active}
                    onMarkerClick={() => {}}
                  />
                </MapProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderTrackingRealTime;
